import { useQuery } from 'react-query';
import { Co2ReportService, ReportingService } from '~/api/services/reporting';
import { onError } from '~/utils/helpers';

const getTradingData = (prefix, data?) => useQuery(
  'reporting-trading-activities',
  () => ReportingService(prefix).getTradingActivityData(data),
  {
    onError,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getTradingSettings = (prefix, data?) => useQuery(
  'reporting-trading-settings',
  () => ReportingService(prefix).getSettingsData(),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getFixedPriceSettings = (prefix) => useQuery(
  'reporting-fixed-price-settings',
  () => ReportingService(prefix).getFixedPriceSettings(),
  {
    cacheTime: 0,
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getFixedPriceData = (prefix, data?) => useQuery(
  'reporting-fixed-price-data',
  () => ReportingService(prefix).getFixedPriceData(data),
  {
    onError,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getArticleBookingsSettings = () => useQuery(
  'reporting-article-settings-data',
  ReportingService().getArticleBookingsSettings,
  {
    onError,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getArticleBookings = (data) => useQuery(
  'reporting-articles-data',
  () => ReportingService().getArticleBookings(data),
  {
    onError,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getArticleBookingFarmers = (data) => useQuery(
  'reporting-articles-farmers-data',
  () => ReportingService().getArticleBookingFarmers(data),
  {
    onError,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getDistrictDeliverySettings = () => useQuery(
  'reporting-district-delivery-settings',
  ReportingService().getDistrictDeliverySettings,
  {
    onError,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getDistrictDeliveries = (data, enabled) => useQuery(
  'reporting-district-delivery-data',
  () => enabled && ReportingService().getDistrictDeliveries(data),
  {
    onError,
    enabled: enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getDistrictDeliverieDetails = (id, data) => useQuery(
  'reporting-district-delivery-details',
  () => ReportingService().getDistrictDeliverieDetails(id, data),
  {
    onError,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getCo2ReportData = (data) => useQuery(
  'reporting-co2-data',
  () => data && Co2ReportService.co2ReportData({
    ...data,
    administrativeDistrictId: data?.administrativeDistrictId?.filter(
      (id) => id !== 'selectAllRegions'
    )
  }),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data?.data
    }
  }
)

const getCo2ReportSettingsFar = () => useQuery(
  'reporting-co2-settings-farmer',
  () => Co2ReportService.co2ReportSettingsFarm(),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getCo2ReportSettingsAdmin = () => useQuery(
  'reporting-co2-settings-admin',
  () => Co2ReportService.co2ReportSettingsAdmin(),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getCo2ReportFootprint = (data) => useQuery(
  'reporting-co2-footprint',
  () => Co2ReportService.co2Footprint(data),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getClimaCheckReportFootprint = (data) => useQuery(
  'reporting-clima-check-footprint',
  () => Co2ReportService.climaCheckFootprint(data),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getClimaCheckReportData = (data) => useQuery(
  'reporting-clima-check-data',
  () => data && Co2ReportService.climaCheckReportData({
    ...data,
    administrativeDistrictId: data?.administrativeDistrictId?.filter(
      (id) => id !== 'selectAllRegions'
    )
  }),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data?.data
    }
  }
)

const getClimaCheckReportSettingsFar = () => useQuery(
  'reporting-clima-check-settings-farmer',
  () => Co2ReportService.climaCheckReportSettingsFarm(),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

const getClimaCheckReportSettingsAdmin = () => useQuery(
  'reporting-clima-check-settings-admin',
  () => Co2ReportService.climaCheckReportSettingsAdmin(),
  {
    onError,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
    select: (data) => {
      return data.data
    }
  }
)

export const ReportingQuery = {
  getTradingData,
  getTradingSettings,
  getFixedPriceData,
  getFixedPriceSettings,
  getArticleBookingsSettings,
  getArticleBookingFarmers,
  getArticleBookings,
  getDistrictDeliveries,
  getDistrictDeliverySettings,
  getDistrictDeliverieDetails,
  getCo2ReportData,
  getCo2ReportSettingsFar,
  getCo2ReportSettingsAdmin,
  getCo2ReportFootprint,
  getClimaCheckReportFootprint,
  getClimaCheckReportSettingsAdmin,
  getClimaCheckReportSettingsFar,
  getClimaCheckReportData
}